/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useRef, useState } from "react";

import styled from "styled-components";

import { InviteCard, INVITE_CARD_WIDTH } from "src/components/Invites/InviteCard";
import { StandardButton } from "src/components/UI/Buttons/StandardButton";
import { Download as DownloadIcon } from "src/components/UI/Icons";

interface Props {
  inviteCode: { mnemonic: string; expiration: Date };
  handleClose: () => void;
}

export function TokenScreen({ inviteCode, handleClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    canvas?.toBlob((blob) => {
      if (blob) {
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = data;
        link.download = `${inviteCode.mnemonic}.png`;
        link.click();
      }
    }, "image/png");
    setHasDownloaded(true);
  };

  return (
    <Flexbox vertical centered style={{ width: "100%" }}>
      <InviteCardWrapper>
        {/*canvas to draw invite image */}
        <InviteCard invite={inviteCode} ref={canvasRef} />
      </InviteCardWrapper>
      <StandardButton
        styles={{ width: INVITE_CARD_WIDTH, marginBottom: "20px", maxWidth: "100%" }}
        onClick={handleDownload}
      >
        <div style={{ position: "relative", right: "3px" }}>
          <DownloadIcon />
        </div>
        Download
      </StandardButton>
      <Note>To invite a friend, send them this card. They will need it to sign up.</Note>
      <Note>
        Note that this card <b>cannot</b> be recovered. Please download now.
      </Note>
      <StandardButton
        onClick={hasDownloaded ? handleClose : undefined}
        disabled={!hasDownloaded}
        styles={{ marginLeft: "auto" }}
      >
        Done
      </StandardButton>
    </Flexbox>
  );
}

const InviteCardWrapper = styled.div`
  margin: 20px;
  display: flex;
  justify-content: center;
`;

// testing out a concept (generic flexbox)
const Flexbox = styled.div<{ vertical?: boolean; centered?: boolean }>`
  display: flex;
  flex-direction: ${({ vertical }) => (vertical ? "column" : "row")};
  align-items: ${({ vertical, centered }) => (vertical && centered ? "center" : "flex-start")};
  justify-content: ${({ vertical, centered }) => (!vertical && centered ? "center" : "flex-start")};
`;

const Note = styled.p`
  font-family: "Inter";
  margin-bottom: 20px;
  width: 100%;
`;
